import { OPTIONS_KEY_CONFIGURABLE } from '@bluheadless/ui-logic/src/constants'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { cx } from '@emotion/css'
import { Grid } from '@mui/material'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Typography from '../../atoms/typography'
import Sizebay from '../../organisms/sizebay/sizebay'
import SizeGuide from '../../organisms/size-guide'
import { useProductPriceProps } from '../product-price'
import { ControlledSwatchGroup, ControlledTextSwatchGroup } from '../swatch'
import { Container, ControlledDropdown, Label, OptionStyled, Stack } from './configurable.styled'

const SizeGuideAction = ({ attributeCode, productName, productPriceProps, sizeGuideProps }) => {
	const {
		catalog: {
			products: {
				attributesMapping: { size },
			},
		},
	} = useConfig()
	if (!size.includes(attributeCode)) return null
	return <SizeGuide name={productName} priceProps={productPriceProps} {...sizeGuideProps} />
}

const SizebayAction = ({ attributeCode }) => {
	const {
		catalog: {
			products: {
				attributesMapping: { size },
			},
		},
	} = useConfig()
	if (!size.includes(attributeCode)) return null
	return <Sizebay />
}

const Option = ({
	renderer,
	attributeCode,
	attributeId,
	label,
	values,
	sizeGuideProps,
	attributesProductImage,
	onClick,
	isSticky,
}) => {
	const {
		catalog: {
			products: { placeholder },
		},
	} = useConfig()

	const productContextValue = useProductContext()
	const productPriceProps = useProductPriceProps()

	const [{ product: { name } = {}, configurableChildrenProducts }, { configurableOptionAvailability }] =
		productContextValue

	const { formatMessage } = useIntl()

	const findAssetByAttributeCode = useCallback(
		(attributeCode, attributeValue) => {
			const product = configurableChildrenProducts?.find(
				(product) => product.attributes[attributeCode]?.id === attributeValue && product.assets?.length > 0
			)

			const swatchAsset = product?.assets?.find((asset) => asset.roles.includes('swatch_image'))

			return {
				src: swatchAsset ? (swatchAsset.type === 'video' ? swatchAsset.video : swatchAsset.image) : placeholder,
				type: swatchAsset?.type || 'image',
			}
		},
		[configurableChildrenProducts, placeholder]
	)

	switch (renderer) {
		case 'select':
			return (
				<Container>
					<ControlledDropdown
						isSticky={isSticky}
						className={cx('ProductOptions', 'select')}
						data-attribute-code={attributeCode}
						label={!isSticky ? label + ':' : null}
						name={`${OPTIONS_KEY_CONFIGURABLE}._${attributeId}`}
						margin="none"
						options={values.map(({ defaultLabel, label, value }) => {
							const optionAvailability = configurableOptionAvailability(value, attributeId)
							return {
								label: (
									<Stack direction="row" justifyContent="space-between">
										<OptionStyled
											notInStockOrNotAvailable={optionAvailability.notInStock || optionAvailability.notAvailable}
										>
											{label || defaultLabel}
										</OptionStyled>

										{optionAvailability.notInStock ? (
											<Label>{formatMessage({ id: 'option_not_in_stock' })}</Label>
										) : optionAvailability.notAvailable ? (
											<Label>{formatMessage({ id: 'option_not_available' })}</Label>
										) : null}
									</Stack>
								),
								value,
								disabled: optionAvailability.notAvailable || false,
								className: optionAvailability.notInStock ? 'out-of-stock' : '',
							}
						})}
						renderValue={(selectedOption) => {
							const { label: _label, value } = values.find((option) => option.value === selectedOption) || {}
							const optionAvailability = configurableOptionAvailability(value, attributeId)
							return _label && value ? (
								<>
									{_label}
									{optionAvailability.notInStock
										? formatMessage({ id: 'option_not_in_stock' })
										: optionAvailability.notAvailable
										? formatMessage({ id: 'option_not_available' })
										: ''}
								</>
							) : (
								label
							)
						}}
						placeholder={label}
						required
						helperText={false}
						disabled={false}
					/>
					<SizebayAction attributeCode={attributeCode} />
					{!isSticky && (
						<SizeGuideAction
							attributeCode={attributeCode}
							productName={name}
							productPriceProps={productPriceProps}
							sizeGuideProps={sizeGuideProps}
						/>
					)}
				</Container>
			)
		case 'visual':
			return (
				<Grid display="flex" flexDirection="column" alignItems="flex-start">
					<Typography gutterBottom color="grey.main" variant="caption">
						{label}
					</Typography>
					<ControlledSwatchGroup
						className={cx('ProductOptions', 'visual')}
						data-attribute-code={attributeCode}
						name={`${OPTIONS_KEY_CONFIGURABLE}._${attributeId}`}
						items={values.map(({ defaultLabel, label, src, value, hex, ...rest }) => {
							const assetByAttributeCode = findAssetByAttributeCode(attributeCode, value)
							const isProductImage = attributesProductImage?.includes(attributeCode)
							const optionAvailability = configurableOptionAvailability(value, attributeId)

							return {
								...rest,
								label: label || defaultLabel,
								value,
								disabled: optionAvailability.notAvailable || false,
								className: optionAvailability.notInStock ? 'out-of-stock' : '',
								src: isProductImage ? assetByAttributeCode.src : src ?? null,
								isProductImage,
								hex: isProductImage ? null : hex,
								type: assetByAttributeCode.type,
								onClick,
							}
						})}
						required
						helperText={false}
						disabled={false}
					/>
					<SizebayAction attributeCode={attributeCode} />
					{!isSticky && (
						<SizeGuideAction
							attributeCode={attributeCode}
							productName={name}
							productPriceProps={productPriceProps}
							sizeGuideProps={sizeGuideProps}
						/>
					)}
				</Grid>
			)
		case 'text':
			return (
				<Grid display="flex" flexDirection="column" alignItems="flex-start">
					{!isSticky && (
						<Typography gutterBottom color="grey.main" variant="caption">
							{label}
						</Typography>
					)}
					<ControlledTextSwatchGroup
						className={cx('ProductOptions', 'text')}
						data-attribute-code={attributeCode}
						name={`${OPTIONS_KEY_CONFIGURABLE}._${attributeId}`}
						items={values.map(({ defaultLabel, label, value, ...rest }) => {
							const optionAvailability = configurableOptionAvailability(value, attributeId)

							return {
								...rest,
								label: label || defaultLabel,
								value,
								disabled: optionAvailability.notAvailable || false,
								className: optionAvailability.notInStock ? 'out-of-stock' : '',
							}
						})}
						helperText={false}
						disabled={false}
						required
					/>
					<SizebayAction attributeCode={attributeCode} />
					{!isSticky && (
						<SizeGuideAction
							attributeCode={attributeCode}
							productName={name}
							productPriceProps={productPriceProps}
							sizeGuideProps={sizeGuideProps}
						/>
					)}
				</Grid>
			)
		default:
			return null
	}
}

const ConfigurableOptions = ({ configurableOptions, sizeGuideProps, attributesProductImage, onClick, isSticky }) => {
	return configurableOptions?.map(({ attributeId, type, ...rest }) => {
		return (
			<Option
				type={type}
				key={attributeId}
				attributeId={attributeId}
				sizeGuideProps={sizeGuideProps}
				attributesProductImage={attributesProductImage}
				onClick={onClick}
				isSticky={isSticky}
				{...rest}
			/>
		)
	})
}

export default ConfigurableOptions
